import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuth } from '../hooks/useAuth';

const StripeSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(15);

  const { isCursoPeriodoActivo, cursoPeriodoActual, isLicencesPremium, profileComplete, profileLoading } = useAuth();
  const sessionId = searchParams.get('session_id');
  const postPaymentTarget = profileLoading || profileComplete ? '/dashboard' : '/onboarding';

  // Estado de activación en tiempo real (el webhook de Stripe otorga el acceso).
  const estado = isCursoPeriodoActivo
    ? {
        icon: '✅',
        title: `Acceso a cursos del periodo "${cursoPeriodoActual}" activado`,
        desc: 'Ya tienes acceso a todos los cursos del periodo en curso. Serás redirigido a tu dashboard.',
      }
    : isLicencesPremium
    ? {
        icon: '✅',
        title: 'Licencia activada',
        desc: 'Tu licencia se activó correctamente. Serás redirigido a tu dashboard.',
      }
    : {
        icon: '⚡',
        title: 'Activación en proceso',
        desc: 'Tu acceso se está activando automáticamente (unos segundos). Si la página redirige antes, refresca el dashboard y aparecerá activo.',
      };

  useEffect(() => {
    // Redirigir automáticamente al login/dashboard después de unos segundos
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Si hay sesión activa, ir al dashboard; si no, al login
          const user = auth.currentUser;
          if (user) {
            navigate(profileComplete ? '/dashboard' : '/onboarding');
          } else {
            navigate('/login');
          }
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, profileComplete]);

  return (
    <div id="stripe-success-page" style={styles.page}>
      <div style={styles.card}>
        {/* Icono de éxito animado */}
        <div style={styles.successIcon}>
          <div style={styles.checkCircle}>
            <span style={styles.checkMark}>✓</span>
          </div>
        </div>

        <h1 style={styles.title}>¡Pago Completado!</h1>
        <p style={styles.subtitle}>
          Tu pago fue procesado exitosamente por Stripe.
          {sessionId ? ` (sesión: ${sessionId.slice(0, 12)}…)` : ''}
        </p>

        <div style={styles.infoBox}>
          <div style={styles.infoStep}>
            <span style={styles.stepIcon}>{estado.icon}</span>
            <div>
              <p style={styles.stepTitle}>{estado.title}</p>
              <p style={styles.stepDesc}>{estado.desc}</p>
            </div>
          </div>
          <div style={styles.infoStep}>
            <span style={styles.stepIcon}>📧</span>
            <div>
              <p style={styles.stepTitle}>Revisa tu correo</p>
              <p style={styles.stepDesc}>
                Stripe enviará un recibo a tu correo electrónico con los detalles del pago.
              </p>
            </div>
          </div>
          <div style={styles.infoStep}>
            <span style={styles.stepIcon}>🎓</span>
            <div>
              <p style={styles.stepTitle}>¡Listo para estudiar!</p>
              <p style={styles.stepDesc}>
                Una vez activada tu licencia, tendrás acceso a todos los simuladores,
                quizzes y material de estudio.
              </p>
            </div>
          </div>
        </div>

        <div style={styles.timerBox}>
          <p style={styles.timerText}>
            Redirigiendo al dashboard en{' '}
            <span style={styles.timerCount}>{countdown}</span> segundos...
          </p>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${((15 - countdown) / 15) * 100}%`,
              }}
            />
          </div>
        </div>

        <div style={styles.btnGroup}>
          <button
            id="btn-go-dashboard"
            onClick={() => navigate(isCursoPeriodoActivo ? '/cursos' : postPaymentTarget)}
            style={styles.primaryBtn}
          >
            {isCursoPeriodoActivo ? 'Ir a Cursos →' : 'Ir al Dashboard →'}
          </button>
          <button
            id="btn-go-login"
            onClick={() => navigate('/login')}
            style={styles.secondaryBtn}
          >
            Iniciar Sesión
          </button>
        </div>

        <p style={styles.supportNote}>
          ¿Tienes algún problema?{' '}
          <a
            href="https://wa.me/2296847814"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            Contáctanos por WhatsApp
          </a>
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a1a2a 0%, #0f2035 50%, #0a1a2a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'var(--font-primary)',
  },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '24px',
    padding: '3rem 2.5rem',
    maxWidth: '560px',
    width: '100%',
    textAlign: 'center',
    backdropFilter: 'blur(10px)',
  },
  successIcon: {
    marginBottom: '1.5rem',
  },
  checkCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    boxShadow: '0 0 30px rgba(76,175,80,0.4)',
    animation: 'pulse 2s infinite',
  },
  checkMark: {
    fontSize: '2.5rem',
    color: 'white',
    fontWeight: '800',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '800',
    background: 'linear-gradient(90deg, #4caf50 0%, #a5d6a7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: '0 0 0.75rem 0',
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: '1rem',
    marginBottom: '2rem',
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '2rem',
    textAlign: 'left',
  },
  infoStep: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '12px',
    padding: '1rem',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  stepIcon: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  stepTitle: {
    color: '#f5f5f5',
    fontWeight: '600',
    margin: '0 0 4px 0',
    fontSize: '0.9rem',
  },
  stepDesc: {
    color: 'var(--text-secondary)',
    fontSize: '0.82rem',
    margin: 0,
    lineHeight: '1.5',
  },
  timerBox: {
    marginBottom: '1.5rem',
  },
  timerText: {
    color: 'var(--text-muted)',
    fontSize: '0.85rem',
    marginBottom: '8px',
  },
  timerCount: {
    color: '#dbb96a',
    fontWeight: '700',
  },
  progressBar: {
    height: '4px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, var(--gold-matte), var(--gold-light))',
    borderRadius: '4px',
    transition: 'width 1s linear',
  },
  btnGroup: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  primaryBtn: {
    flex: 1,
    background: 'linear-gradient(135deg, #c5a059 0%, #dbb96a 100%)',
    border: 'none',
    padding: '13px 20px',
    color: '#0a1a2a',
    borderRadius: '10px',
    fontSize: '0.95rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    flex: 1,
    background: 'transparent',
    border: '1.5px solid rgba(255,255,255,0.2)',
    padding: '13px 20px',
    color: 'var(--text-secondary)',
    borderRadius: '10px',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
  supportNote: {
    color: 'var(--text-muted)',
    fontSize: '0.8rem',
  },
  link: {
    color: '#dbb96a',
    textDecoration: 'none',
  },
};

export default StripeSuccess;
